import React, { useEffect, useState } from "react";
import { api, fmtMoney, AUCTION_HOUSES } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Gavel } from "lucide-react";

export default function Auctions() {
  const [vehicles, setVehicles] = useState([]);

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)); }, []);

  const groups = AUCTION_HOUSES.map((h) => {
    const list = vehicles.filter((v) => v.auction_house === h);
    const spend = list.reduce((s, v) => s + (v.total_investment || 0), 0);
    return { house: h, list, spend, count: list.length };
  });

  return (
    <div data-testid="auctions-page">
      <PageHeader overline="Acquisitions" title="Auction Sources" description="Buying performance across Copart, IAAI, Manheim, Adesa and private sellers." />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {groups.map((g) => (
          <Card key={g.house} className="p-5 card-hover" data-testid={`auction-card-${g.house}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Source</div>
                <div className="font-display text-xl font-bold mt-1">{g.house}</div>
              </div>
              <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center text-blue-600 dark:text-blue-400">
                <Gavel size={18} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-border">
              <div>
                <div className="text-xs text-muted-foreground">Vehicles</div>
                <div className="font-display text-2xl font-bold">{g.count}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Total Spend</div>
                <div className="font-display text-2xl font-bold">{fmtMoney(g.spend)}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card className="overflow-x-auto">
        <div className="px-5 pt-5">
          <h3 className="font-display text-xl font-semibold">All Auction Purchases</h3>
          <p className="text-sm text-muted-foreground">Detailed view of every acquisition with itemized fees.</p>
        </div>
        <table className="w-full text-sm mt-4">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Stock", "Vehicle", "Source", "Purchase", "Fees", "Investment"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {vehicles.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No purchases yet.</td></tr>}
            {vehicles.map((v) => {
              const fees = (v.auction_fees || 0) + (v.transport_fees || 0) + (v.gate_fees || 0) + (v.documentation_fees || 0) + (v.storage_fees || 0) + (v.buyer_fees || 0);
              return (
                <tr key={v.id} className="border-t border-border hover:bg-secondary/40">
                  <td className="px-4 py-3 font-mono text-xs">{v.stock_number}</td>
                  <td className="px-4 py-3 font-medium">{v.year} {v.make} {v.model}</td>
                  <td className="px-4 py-3">{v.auction_house || "—"}</td>
                  <td className="px-4 py-3">{fmtMoney(v.purchase_price)}</td>
                  <td className="px-4 py-3 text-red-600">{fmtMoney(fees)}</td>
                  <td className="px-4 py-3 font-semibold">{fmtMoney(v.total_investment)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
